/*
 * coupon_apply / coupon_fail — the cart page's promo code entry. (#173, GA4-E5-08)
 *
 * WHERE THE ENTRY IS
 * `sections/cart-promo.liquid` ("Have a promo code?" on the cart page) applies a code by navigating
 * to `/discount/<CODE>?redirect=/cart`. #173 first closed on the finding that no on-site entry
 * existed; the section is named *promo*, not *discount*, so the search missed it (see #173, #698).
 *
 * WHY THE EVENT IS PUSHED ON THE PAGE AFTER THE CLICK
 * Apply navigates away at once, so a push on the click page would be torn down before GTM's tag got
 * the hit out: the #383 lesson from the pincode popup. The section calls `rememberAttempt(code)`
 * before navigating, and this file reports it when the cart page comes back, once.
 *
 * HOW SUCCESS AND FAILURE ARE TOLD APART
 * The redirect itself says nothing. `/cart.js` does: after a `/discount/` visit its `discount_codes`
 * lists the code with `applicable: true` when it applied and `false` when it did not (checked on
 * om-staging, 1 Oct: an unknown code came back `false`, APP15 `true` with 15% off). So:
 *   applicable          → coupon_apply
 *   not applicable / absent → coupon_apply, then coupon_fail
 * `coupon_apply` is the attempt (the Apply click), as the tracking plan defines it; `coupon_fail` is
 * the outcome. WHY a code failed (expired, minimum not met, invalid) is not in the response, which
 * is why `coupon_failure_reason` stays unregistered.
 *
 * WHAT IS SENT AS `coupon`
 * The field is free text, and a shopper can type anything into it, including an email address. Only
 * something shaped like a code is sent; anything else is sent as `(redacted)` so the attempt still
 * counts without the text leaving the site.
 *
 * FAILURE POSTURE
 * Every step is guarded. Measurement must never be able to stop a code being applied.
 */
(function () {
  'use strict';

  var KEY = 'om_coupon_attempt';
  var TTL_MS = 5 * 60 * 1000;
  var CODE_SHAPE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,39}$/;

  var OMCouponAnalytics = {};

  function store() {
    try { return window.sessionStorage; } catch (e) { return null; }
  }

  /** What may be sent to GA4 for a typed code. */
  OMCouponAnalytics.safeCode = function (raw) {
    var code = String(raw == null ? '' : raw).trim();
    if (!code) return null;
    return CODE_SHAPE.test(code) ? code.toUpperCase() : '(redacted)';
  };

  /** Called by cart-promo.liquid just before it navigates to /discount/. */
  OMCouponAnalytics.rememberAttempt = function (code, s) {
    s = s || store();
    var raw = String(code == null ? '' : code).trim();
    if (!s || !raw) return;
    try { s.setItem(KEY, JSON.stringify({ code: raw, at: new Date().getTime() })); } catch (e) { /* unreported */ }
  };

  /** Consume before validating, so a stale or malformed marker cannot attribute a later page. */
  OMCouponAnalytics.takeAttempt = function (s, now) {
    if (!s) return null;
    var raw;
    try { raw = s.getItem(KEY); } catch (e) { return null; }
    if (!raw) return null;
    try { s.removeItem(KEY); } catch (e) { /* nothing useful to do */ }
    var m;
    try { m = JSON.parse(raw); } catch (e) { return null; }
    if (!m || !m.code || !m.at || (now - m.at) > TTL_MS) return null;
    return m;
  };

  /** The events one attempt produces, given the /cart.js body. */
  OMCouponAnalytics.eventsFor = function (attempt, cart) {
    if (!attempt || !attempt.code) return [];
    var coupon = OMCouponAnalytics.safeCode(attempt.code);
    if (!coupon) return [];
    var wanted = String(attempt.code).trim().toUpperCase();
    var codes = (cart && cart.discount_codes) || [];
    var applied = false;
    for (var i = 0; i < codes.length; i++) {
      if (codes[i] && String(codes[i].code || '').toUpperCase() === wanted && codes[i].applicable === true) applied = true;
    }
    var events = [{ event: 'coupon_apply', coupon: coupon }];
    if (!applied) events.push({ event: 'coupon_fail', coupon: coupon });
    return events;
  };

  window.OMCouponAnalytics = OMCouponAnalytics;

  if (typeof document === 'undefined' || !window.fetch) return;

  var attempt = OMCouponAnalytics.takeAttempt(store(), new Date().getTime());
  if (!attempt) return;

  try {
    window.fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        var events = OMCouponAnalytics.eventsFor(attempt, cart);
        window.dataLayer = window.dataLayer || [];
        for (var i = 0; i < events.length; i++) window.dataLayer.push(events[i]);
      })
      .catch(function () { /* measurement only */ });
  } catch (e) { /* measurement only */ }
})();
